import {
  ArrowsAltOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  CheckSquareFilled,
  MinusSquareOutlined,
} from '@ant-design/icons'
import { findValueInRecord } from '@salescore/buff-common'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { useMe } from '@salescore/client-common'
import type { SheetCell, ViewQueryRecordNode, ViewQueryRecordNodeWithParent } from '@salescore/core'
import { organizationHasFeature } from '@salescore/frontend-common'
import { type CSSProperties, type ReactElement, useState } from 'react'
import { useLongPress } from 'react-use'
import { useRecoilState } from 'recoil'
import { z } from 'zod'

import type { RSheetRecordNode, SheetColumn, SheetSystemColumn } from '../../../domain/column.ts'
import type { RSheetsCursorPosition } from '../../../domain/cursor/cursorTypes.ts'
import { useCursorMutation } from '../../../recoil/cursor.ts'
import { pickedIdsAtom } from '../../../recoil/records/atoms.ts'
import type { RowConfig } from '../../../recoil/rowConfig.ts'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms.ts'
import { openContextMenu, toggleEditMode } from '../../../rsheet/components/body/cell/cellAction.ts'
import type { SetMainCursorByRecordIdArguments } from '../../../rsheet/hooks/useSetMainCursorByRecordId.ts'
import { getEmptyRecordCellBackgroundColor, RSheetsStyle } from '../../../rsheet/util/RSheetsStyle.ts'
import { SheetThreadsFormPopoverIfAllowed } from '../../form/SheetThreadsFormPopoverIfAllowed.tsx'

export const SheetSystemCell = ({
  sheetCell,
  rootRecordNode,
  column,
  rowIndex,
  columnIndex,
  expandable,
  expanded,
  isFieldChanged,
  rowHeight,
  columns,
  setMainCursorByRecordId,
  setRowConfig,
  view,
  setDragging,
  expandOnDrag,
  ability,
}: {
  sheetCell: SheetCell
  rootRecordNode: RSheetRecordNode
  column: SheetSystemColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  expandable: boolean
  expanded: boolean
  isFieldChanged: boolean
  rowHeight: number
  columns: Array<SheetSystemColumn<ViewQueryRecordNode> | SheetColumn<ViewQueryRecordNode>>
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  setRowConfig: (id: string, updater: (oldValue: RowConfig) => RowConfig) => void
  view: ViewFieldsFragment
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { innerRowIndexStart, height, recordNode } = sheetCell
  const { width } = column
  const { selectRowCell } = useCursorMutation(ability)
  const [isCommentActive, setIsCommentActive] = useState(false) // 行にコメント入力中かの判定

  const defaultStyle: CSSProperties = {
    width,
    height: height * rowHeight,
    position: 'relative',
    overflow: 'hidden',
    transition: RSheetsStyle.cellTransition,
    border: `1px ${RSheetsStyle.color.border} solid`,
  }

  switch (column.type) {
    case 'rowIndex': {
      const nameParsed = z.string().safeParse(findValueInRecord(rootRecordNode.attributes, (k) => k.endsWith('_name')))
      return (
        <div
          style={{
            ...defaultStyle,
            fontSize: 10,
            textAlign: 'center',
            paddingTop: 15,
            color: '#aaa',
          }}
          onClick={() => {
            // コメント機能の操作中（アイコンへのホバーまたはPopover表示中）はセルの選択を行わない
            if (isCommentActive) {
              return
            }
            selectRowCell({ rowIndex, rootRecordNode, columns })
          }}
        >
          <SheetThreadsFormPopoverIfAllowed
            setIsCommentActive={setIsCommentActive}
            recordId={rootRecordNode.id}
            recordName={nameParsed.success ? nameParsed.data : undefined}
            view={view}
          >
            <div
              style={{
                fontSize: 10,
                textAlign: 'center',
                color: '#aaa',
              }}
            >
              {rowIndex + 1}
            </div>
          </SheetThreadsFormPopoverIfAllowed>
          {expandable && (
            <div
              className="absolute inset-x-0 bottom-0 cursor-pointer"
              onClick={() => {
                setRowConfig(rootRecordNode.id ?? '', (x) => ({ ...x, expanded: !expanded }))
              }}
            >
              {expanded ? <CaretUpOutlined /> : <CaretDownOutlined />}
            </div>
          )}
        </div>
      )
    }
    case 'rowCheckbox': {
      return (
        <ControlCell
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          innerRowIndex={innerRowIndexStart}
          defaultStyle={defaultStyle}
          expandOnDrag={expandOnDrag}
          recordNode={recordNode}
          innerRowIndexStart={innerRowIndexStart}
          column={column}
          rootRecordNode={rootRecordNode}
          setMainCursorByRecordId={setMainCursorByRecordId}
          isFieldChanged={isFieldChanged}
          height={height}
          cellBody={rowCheckboxCellBody}
          setDragging={setDragging}
          ability={ability}
        />
      )
    }
    case 'open': {
      return (
        <ControlCell
          rowIndex={rowIndex}
          columnIndex={columnIndex}
          innerRowIndex={innerRowIndexStart}
          defaultStyle={defaultStyle}
          expandOnDrag={expandOnDrag}
          recordNode={recordNode}
          innerRowIndexStart={innerRowIndexStart}
          column={column}
          rootRecordNode={rootRecordNode}
          setMainCursorByRecordId={setMainCursorByRecordId}
          isFieldChanged={isFieldChanged}
          height={height}
          setDragging={setDragging}
          cellBody={openCellBody}
          ability={ability}
        />
      )
    }
  }
}

const rowCheckboxCellBody = (recordNode: ViewQueryRecordNodeWithParent): ReactElement => (
  <RowCheckboxCell recordNode={recordNode} />
)
const openCellBody = (): ReactElement => <ArrowsAltOutlined style={{ color: '#ddd' }} className="cursor-pointer" />

const ControlCell = ({
  recordNode,
  column,
  rowIndex,
  columnIndex,
  innerRowIndex,
  rootRecordNode,
  setMainCursorByRecordId,
  isFieldChanged,
  height,
  defaultStyle,
  expandOnDrag,
  innerRowIndexStart,
  cellBody,
  setDragging,
  ability,
}: {
  recordNode: ViewQueryRecordNodeWithParent | undefined
  column: SheetSystemColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  rootRecordNode: RSheetRecordNode
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  isFieldChanged: boolean
  height: number
  defaultStyle: CSSProperties
  expandOnDrag: (position: RSheetsCursorPosition) => void
  innerRowIndexStart: number
  cellBody: (recordNode: ViewQueryRecordNodeWithParent) => ReactElement
  setDragging: () => void
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement =>
  recordNode === undefined ? (
    <EmptyCell
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      innerRowIndex={innerRowIndexStart}
      defaultStyle={defaultStyle}
      expandOnDrag={expandOnDrag}
      setDragging={setDragging}
      ability={ability}
    />
  ) : (
    <RecordCell
      column={column}
      rowIndex={rowIndex}
      columnIndex={columnIndex}
      innerRowIndex={innerRowIndex}
      rootRecordNode={rootRecordNode}
      setMainCursorByRecordId={setMainCursorByRecordId}
      isFieldChanged={isFieldChanged}
      height={height}
      defaultStyle={defaultStyle}
      expandOnDrag={expandOnDrag}
      cellBody={cellBody(recordNode)}
      setDragging={setDragging}
    />
  )

const RecordCell = ({
  column,
  rowIndex,
  columnIndex,
  innerRowIndex,
  rootRecordNode,
  setMainCursorByRecordId,
  isFieldChanged,
  height,
  defaultStyle,
  setDragging,
  expandOnDrag,
  cellBody,
}: {
  column: SheetSystemColumn<ViewQueryRecordNode>
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  rootRecordNode: RSheetRecordNode
  setMainCursorByRecordId: (arguments_: SetMainCursorByRecordIdArguments) => void
  isFieldChanged: boolean
  height: number
  defaultStyle: CSSProperties
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  cellBody: ReactElement
}): ReactElement => {
  const { organization } = useMe()

  const changed = isFieldChanged
  const mobileContextMenuEnabled = organizationHasFeature(organization, 'enable_mobile_context_menu')
  const longPressEventHandler = useLongPress(
    (event) => {
      if (!('touches' in event) || event.touches[0] === undefined) {
        return
      }
      const contextMenuPosition = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      }
      openContextMenu({
        rootRecordNode,
        columnIndex,
        innerRowIndex,
        contextMenuPosition,
        disabled: false,
        setMainCursorByRecordId,
      })
    },
    {
      isPreventDefault: false,
      delay: 300,
    },
  )

  return (
    <div
      key={column.key}
      data-e2e={`rsheet-cell-${rowIndex}-${columnIndex}-${innerRowIndex}`}
      style={{
        ...defaultStyle,
        padding: '10px 10px',
        ...(changed ? { backgroundColor: 'rgba(0,0,0,0.06)' } : { backgroundColor: 'inherit' }),
        fontWeight: undefined,
        opacity: 1,
        fontSize: 14,
        ...(height === 1
          ? {
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }
          : {}),
      }}
      {...(mobileContextMenuEnabled ? longPressEventHandler : {})}
      onContextMenu={(event) => {
        const contextMenuPosition = {
          x: event.clientX,
          y: event.clientY,
        }
        openContextMenu({
          rootRecordNode,
          columnIndex,
          innerRowIndex,
          contextMenuPosition,
          disabled: false,
          setMainCursorByRecordId,
        })
        event.preventDefault()
        event.stopPropagation()
      }}
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey && rootRecordNode.id !== undefined) {
          setMainCursorByRecordId({
            position: {
              recordId: rootRecordNode.id,
              columnIndex,
              innerRowIndex,
            },
            option: {
              expand: true,
            },
          })
          return
        }
        toggleEditMode({ rootRecordNode, columnIndex, innerRowIndex, disabled: false, setMainCursorByRecordId })
      }}
    >
      {cellBody}
    </div>
  )
}

const EmptyCell = ({
  rowIndex,
  columnIndex,
  innerRowIndex,
  defaultStyle,
  setDragging,
  expandOnDrag,
  ability,
}: {
  rowIndex: number
  columnIndex: number
  innerRowIndex: number
  defaultStyle: CSSProperties
  setDragging: () => void
  expandOnDrag: (position: RSheetsCursorPosition) => void
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { expand, setContextMenuPosition } = useCursorMutation(ability)

  return (
    <div
      style={{
        ...defaultStyle,
        fontSize: 14,
        padding: '10px 10px',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        transition: RSheetsStyle.cellTransition,
        textAlign: 'center',
        color: RSheetsStyle.color.gray,
        backgroundColor: getEmptyRecordCellBackgroundColor(undefined),
      }}
      onPointerEnter={() => {
        expandOnDrag({ rowIndex, columnIndex, innerRowIndex })
      }}
      onPointerDown={(event) => {
        setDragging() // event.stopPropagation()などによりsetDraggingが実行されなくなると、ドラッグによるセルの範囲選択が動作しなくなる。
        // ドラッグによるセルの範囲選択を実現するためには、クリック時にメインカーソルを更新する必要がある。
        // クリックを離す時に発火するonClickだと遅すぎるので、onPointerDownで処理する。
        if (event.shiftKey) {
          expand({
            rowIndex,
            columnIndex,
            innerRowIndex,
          })
        }
      }}
      onContextMenu={(event) => {
        setContextMenuPosition(
          {
            columnIndex,
            rowIndex,
            innerRowIndex,
          },
          {
            x: event.clientX,
            y: event.clientY,
          },
        )
        event.preventDefault()
        event.stopPropagation()
      }}
    />
  )
}

// デフォルトのチェックボックスを使うと、クリックイベントが扱いづらいため、純粋に表示のみを行うコンポーネントとして実装している
const RowCheckboxCell = ({ recordNode }: { recordNode: ViewQueryRecordNodeWithParent }): ReactElement => {
  const [pickedIds] = useRecoilState(pickedIdsAtom)
  const value = recordNode.id === undefined ? false : pickedIds.includes(recordNode.id)
  const style: CSSProperties = {
    fontSize: 16,
    color: value ? '#0D6FDA' : '#ddd',
  }
  return value ? <CheckSquareFilled style={style} /> : <MinusSquareOutlined style={style} />
}
